import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, CheckCircle2, Loader2, UserCog, BarChart3 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { PageHeader, Loading, Empty, DemoBadge, LiveIndicator } from "@/components/common";

export function ResultBars({ results, testid = "survey-results" }) {
  if (!results) return <Loading rows={2} />;
  return (
    <div className="space-y-5" data-testid={testid}>
      <div className="text-xs text-muted-foreground"><span className="font-mono font-semibold text-foreground" data-testid="survey-results-total">{results.total}</span> respuestas</div>
      {results.questions.map((q) => {
        const total = Math.max(1, q.answered);
        return (
          <div key={q.qid}>
            <div className="text-sm font-medium">{q.text}</div>
            {q.type === "text" ? (
              <div className="mt-2 space-y-1">{(q.samples || []).slice(0, 6).map((s, i) => <div key={i} className="text-xs rounded bg-secondary px-2 py-1">“{s}”</div>)}{!q.samples?.length && <div className="text-xs text-muted-foreground">Sin respuestas abiertas.</div>}</div>
            ) : (
              <div className="mt-2 space-y-1.5">
                {q.type === "scale" && q.average != null && <div className="text-xs text-muted-foreground">Promedio: <b className="text-foreground">{q.average}</b> / 5</div>}
                {Object.entries(q.counts).map(([o, n]) => (
                  <div key={o}>
                    <div className="flex justify-between text-xs"><span>{q.type === "scale" ? `${o} ★` : o}</span><span className="font-mono">{n} · {Math.round((n / total) * 100)}%</span></div>
                    <div className="h-2 rounded-full bg-secondary mt-0.5 overflow-hidden"><div className="h-2 rounded-full bg-[#007C91] transition-transform origin-left" style={{ width: `${(n / total) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Surveys() {
  const list = useFetch("/surveys", { poll: 20000 });
  const [cur, setCur] = useState(null);
  const [answers, setAnswers] = useState({});
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const live = useFetch(cur && (cur.answered || results) ? `/surveys/${cur.survey_id}/results` : null, { poll: cur ? 5000 : 0, deps: [cur?.survey_id, !!results] });

  const openS = (s) => { setCur(s); setAnswers({}); setResults(null); };
  const submit = async () => {
    setSending(true);
    try { const { data } = await api.post(`/surveys/${cur.survey_id}/responses`, { answers }); setResults(data); toast.success("¡Gracias! Tu respuesta quedó registrada"); list.reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const showResults = cur && (cur.answered || results || cur.status === "cerrada");

  return (
    <div>
      <PageHeader eyebrow="Participación" title="Encuestas ciudadanas" subtitle="Tu opinión en segundos, sin trámites. Una respuesta por persona y resultados en vivo." />
      {list.loading ? <Loading rows={3} /> : !list.data?.length ? <Card><CardContent><Empty icon={ClipboardList} title="No hay encuestas para tu perfil" hint="Completa tu perfil (colonia, edad, tipo de ciudadano) para recibir encuestas de tu zona." /></CardContent></Card> : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="surveys-list">
          {list.data.map((s) => (
            <Card key={s.survey_id} className="card-hover" data-testid={`survey-card-${s.survey_id}`}>
              <CardContent className="p-5 flex flex-col gap-2 h-full">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold">{s.title} {s.is_demo && <DemoBadge />}</div>
                  {s.answered ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" />Respondida</Badge>
                    : s.status === "cerrada" ? <Badge variant="outline">Cerrada</Badge> : <Badge variant="outline" className="bg-[#DFF3F6] text-[#006A7C] border-[#b9e2e8]">Activa</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">{s.description}</p>
                <div className="text-xs text-muted-foreground">{s.questions.length} pregunta(s) · {s.responses} respuestas</div>
                {!s.eligible && <div className="text-xs rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5" />{s.reason}</div>}
                <div className="mt-auto pt-2">
                  <Button className="w-full btn-press" variant={s.answered || s.status === "cerrada" ? "outline" : "default"} disabled={!s.eligible && !s.answered} onClick={() => openS(s)} data-testid={`survey-open-${s.survey_id}`}>
                    {s.answered || s.status === "cerrada" ? <><BarChart3 className="h-4 w-4 mr-1" />Ver resultados</> : "Responder"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!cur} onOpenChange={(o) => !o && setCur(null)}>
        <DialogContent className="bg-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="survey-dialog">
          {cur && (<>
            <DialogHeader><DialogTitle>{cur.title}</DialogTitle><DialogDescription>{cur.description}</DialogDescription></DialogHeader>
            {showResults ? (<>
              <div className="flex justify-end"><LiveIndicator lastSync={live.lastSync} /></div>
              <ResultBars results={live.data || results} />
            </>) : (
              <div className="space-y-5">
                {cur.questions.map((q, i) => (
                  <div key={q.qid} className="space-y-2" data-testid={`survey-question-${q.qid}`}>
                    <div className="text-sm font-medium">{i + 1}. {q.text}{q.required && <span className="text-rose-600"> *</span>}</div>
                    {q.type === "single" && (
                      <RadioGroup value={answers[q.qid] || ""} onValueChange={(v) => set(q.qid, v)}>
                        {q.options.map((o, j) => <label key={o} className="flex items-center gap-2 text-sm rounded-md border px-3 py-2 cursor-pointer hover:bg-secondary"><RadioGroupItem value={o} data-testid={`survey-${q.qid}-opt-${j}`} />{o}</label>)}
                      </RadioGroup>
                    )}
                    {q.type === "multiple" && q.options.map((o, j) => {
                      const cur_ = answers[q.qid] || [];
                      return <label key={o} className="flex items-center gap-2 text-sm rounded-md border px-3 py-2 cursor-pointer hover:bg-secondary"><Checkbox checked={cur_.includes(o)} onCheckedChange={(v) => set(q.qid, v ? [...cur_, o] : cur_.filter((x) => x !== o))} data-testid={`survey-${q.qid}-opt-${j}`} />{o}</label>;
                    })}
                    {q.type === "scale" && (
                      <div className="flex gap-2">{[1, 2, 3, 4, 5].map((n) => (
                        <Button key={n} type="button" variant={answers[q.qid] === n ? "default" : "outline"} className="h-11 w-11" onClick={() => set(q.qid, n)} data-testid={`survey-${q.qid}-scale-${n}`}>{n}</Button>
                      ))}<span className="text-xs text-muted-foreground self-center ml-1">1 = malo · 5 = excelente</span></div>
                    )}
                    {q.type === "text" && <Textarea rows={2} value={answers[q.qid] || ""} onChange={(e) => set(q.qid, e.target.value)} data-testid={`survey-${q.qid}-text`} />}
                  </div>
                ))}
                <Label className="text-xs text-muted-foreground font-normal block">Solo se permite una respuesta por persona.</Label>
                <Button className="w-full" onClick={submit} disabled={sending} data-testid="survey-submit-button">{sending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Enviar respuesta</Button>
              </div>
            )}
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
